import React from 'react';
import { useChat } from '../../contexts/ChatContext';
import ChatWindow from './ChatWindow';
import { ChatIcon } from './icons/ChatIcons';

/**
 * ChatWidgetContainer - Componente contenedor que conecta ChatWindow con ChatContext
 *
 * Este componente:
 * 1. Obtiene el estado del chat desde ChatContext
 * 2. Renderiza el botón flotante cuando el chat está cerrado
 * 3. Renderiza la ventana de chat cuando está abierto
 */
const ChatWidgetContainer: React.FC = () => {
  const {
    messages,
    isOpen,
    isStreaming,
    isEnabled,
    unreadCount,
    error,
    toggleChat,
    closeChat,
    sendMessage,
    clearChat,
    retryLastMessage,
    autoSpeakEnabled,
    autoSpeakActivatedAt,
    toggleAutoSpeak,
  } = useChat();

  // No renderizar nada si el chat está deshabilitado
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button - Solo mostrar cuando el chat está cerrado */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          data-ar-guide="chat-launcher"
          className="fixed bottom-6 right-6 bg-forest text-paper p-4 rounded-lg shadow-lift hover:bg-forest-mid focus:outline-none focus:ring-4 focus:ring-forest/30 transition-all duration-300 z-40 group"
          aria-label="Abrir chat"
        >
          <ChatIcon className="h-6 w-6" />

          {/* Unread Badge */}
          {unreadCount > 0 && (
            <div className="absolute -top-1 -left-1 bg-danger text-paper text-xs font-bold rounded-pixel h-5 w-5 flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}

          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-ink text-paper text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Asistente de MiCampoDigital
            <div className="absolute top-full right-4 -mt-1 border-4 border-transparent" style={{ borderTopColor: 'var(--cm-ink)' }}></div>
          </div>
        </button>
      )}

      {/* Chat Window - Renderizar cuando está abierto */}
      <ChatWindow
        isOpen={isOpen}
        messages={messages}
        isTyping={isStreaming}
        onClose={closeChat}
        onSendMessage={sendMessage}
        onNewChat={clearChat}
        autoSpeakEnabled={autoSpeakEnabled}
        autoSpeakActivatedAt={autoSpeakActivatedAt}
        onToggleAutoSpeak={toggleAutoSpeak}
        error={error}
        onRetry={retryLastMessage}
      />
    </>
  );
};

export default ChatWidgetContainer;
